import { Box, Fade, Flex, Text } from "@chakra-ui/react"
import React, { useEffect, useState } from "react"
import { SuggestionGroups } from "../../../api-clients/suggest-openapi"
import { suggestSections } from "../../../common/constants/suggest-constants"
import { useBrandedTranslation } from "../../../common/hooks/useBrandedTranslation"
import { isExactArticleMatch } from "../../../common/utils/suggest-utils"
import ArticleSuggestions from "./ArticleSuggestions/ArticleSuggestions"
import ProductSuggestions from "./ProductSuggestions/ProductSuggestions"
import SparePartSuggestions from "./SparePartSuggestions/SparePartSuggestions"
import SuggestionsFooterLink from "./SuggestionsFooterLink"
import TermSuggestions from "./TermSuggestions/TermSuggestions"

type Props = {
  suggestionGroups: Array<SuggestionGroups>
  handleSubmit: React.FormEventHandler
  handleUpdateValue: (val: string) => void
  handleResetSearchField: () => void
  preventBlur: React.MouseEventHandler
  searchTerm: string
  isFocused: boolean
}

const SuggestionsMenu = ({
  suggestionGroups,
  handleSubmit,
  handleUpdateValue,
  handleResetSearchField,
  preventBlur,
  searchTerm,
  isFocused,
}: Props) => {
  const { t } = useBrandedTranslation()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    setIsOpen(isFocused)
  }, [isFocused])

  const handleClickSuggestion = (e, suggestion: string) => {
    setIsOpen(false)
    handleSubmit(e, suggestion)
  }

  // If drop down menu is not open or there is no response
  if (!isOpen || !suggestionGroups) {
    return null
  }

  // Section titles labels
  const sectionLabels = {
    "0terms": "suggest-often-searched",
    "1products": "search-products",
    "2articleproducts": "pdp-nav-article",
    "3spareparttables": "search-spareParts",
    "4categories": "cat-category",
  }

  // Filter out the groups we don't currently show

  const productSuggestions = suggestionGroups.find((group) => group.indexName === suggestSections.PRODUCTS)?.suggestions
  const articleSuggestions = suggestionGroups.find((group) => group.indexName === suggestSections.ARTICLES)?.suggestions

  const hasExactArticleMatch = isExactArticleMatch(productSuggestions, articleSuggestions)

  /* If we have an exact article match, don't show the Products section 
    otherwise remove the articles suggestions section */
  let filteredSuggestionGroups = hasExactArticleMatch
    ? suggestionGroups.filter((group) => group.indexName !== suggestSections.PRODUCTS)
    : suggestionGroups.filter((group) => group.indexName !== suggestSections.ARTICLES)

  /* Remove categories suggestions section for now */
  filteredSuggestionGroups = filteredSuggestionGroups.filter((group) => group.indexName !== suggestSections.CATEGORIES)

  /* If we have any results show the link in the footer otherwise show "No results" label */
  const hasResults = filteredSuggestionGroups.some((group) => group.suggestions.length > 0)

  return (
    <Flex
      as="section"
      flexDirection="column"
      position="absolute"
      w="100%"
      mt="1"
      py="2"
      zIndex="dropdown"
      bgColor="white"
      /* Offset "xl" shadow to hide the top part */
      boxShadow="xlNoTop"
    >
      {hasResults ? (
        <>
          <Box as="section">
            {filteredSuggestionGroups.map((group) => {
              return group.suggestions.length ? (
                <Flex
                  as="section"
                  flexDirection="column"
                  pb="1"
                  key={group.indexName}
                  id={`suggestions-${group.indexName}`}
                  _first={{ pt: "4" }}
                  _last={{ pb: "0" }}
                >
                  {/* Don't put title for the Articles section and don't put title for Products when we have exact article match */}
                  {(group.indexName === suggestSections.TERMS ||
                    group.indexName === suggestSections.PRODUCTS ||
                    group.indexName === suggestSections.SPARE_PARTS) && (
                    <Text fontSize="sm" fontWeight="medium" pb="4" px="2">
                      {t(sectionLabels[group.indexName])}
                    </Text>
                  )}
                  {group.indexName === suggestSections.TERMS && (
                    <Fade in={hasResults}>
                      <TermSuggestions
                        suggestions={group.suggestions}
                        handleClickSuggestion={handleClickSuggestion}
                        handleUpdateValue={handleUpdateValue}
                        preventBlur={preventBlur}
                      />
                    </Fade>
                  )}
                  {group.indexName === suggestSections.PRODUCTS && (
                    <Fade in={hasResults}>
                      <ProductSuggestions
                        suggestions={group.suggestions}
                        handleResetSearchField={handleResetSearchField}
                      />
                    </Fade>
                  )}
                  {group.indexName === suggestSections.ARTICLES && hasExactArticleMatch && (
                    <Fade in={hasResults}>
                      <ArticleSuggestions
                        suggestions={group.suggestions}
                        searchTerm={searchTerm}
                        productSuggestions={productSuggestions}
                        handleResetSearchField={handleResetSearchField}
                      />
                    </Fade>
                  )}
                  {group.indexName === suggestSections.SPARE_PARTS && (
                    <Fade in={hasResults}>
                      <SparePartSuggestions
                        suggestions={group.suggestions}
                        handleResetSearchField={handleResetSearchField}
                      />
                    </Fade>
                  )}
                </Flex>
              ) : null
            })}
          </Box>
          {/* If we have results and a user input, show the footer with the link to submit the search term */}
          {searchTerm && (
            <Fade in={hasResults}>
              <SuggestionsFooterLink preventBlur={preventBlur} handleSubmit={handleSubmit} />
            </Fade>
          )}
        </>
      ) : (
        <Fade in={!hasResults}>
          <Flex h="13">
            <Text fontSize="sm" ml="4" alignSelf="center">
              {t("suggest-no-results")}
            </Text>
          </Flex>
        </Fade>
      )}
    </Flex>
  )
}

export default SuggestionsMenu
